import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from "react";
import {
  fetchExecutiveSummary,
  fetchStalledThreads,
  fetchHitlTimeouts,
  fetchExecutiveGoals,
  fetchVersionHealthStatus,
  type ExecutiveSummary,
  type StalledThread,
  type HitlTimeout,
  type ExecutiveGoal,
  type CapabilityDebugPayload,
  type VersionHealthStatus,
} from "@/api/executive";
import CapabilityGate from "@/components/CapabilityGate";
import LockedFeatureCard from "@/components/LockedFeatureCard";
import { Button } from "@/components/ui/button";
import { MasterAgentsPanel } from "@/components/master/MasterAgentsPanel";
import { MasterHitlPanel } from "@/components/master/MasterHitlPanel";
import { MasterMemoryPanel } from "@/components/master/MasterMemoryPanel";
import { MasterNotificationsPanel } from "@/components/master/MasterNotificationsPanel";
import { MasterTimelinePanel } from "@/components/master/MasterTimelinePanel";
import { useMasterAutopilot } from "@/hooks/master/useMasterAutopilot";
import { useEventStream } from "@/hooks/useEventStream";
import { runAutopilotPass, testAutopilotRule } from "@/api/autopilot";
import { useHasCapability } from "@/hooks/useCapabilities";
import { useCapabilityContext } from "@/providers/CapabilitiesProvider";
import { useMasterAgents } from "@/hooks/master/useMasterAgents";
import { useMasterDigest } from "@/hooks/master/useMasterDigest";
import { planLabel } from "@/lib/plans";

const FEATURE_LABELS = {
  dashboard: "Executive dashboard",
  workflowStats: "Workflow stats",
  stalledThreads: "Stalled threads insights",
  hitlTimeouts: "HITL timeout reporting",
  goals: "Executive goals",
  autopilotPass: "Autopilot sweeps",
  autopilotRule: "Autopilot rule dispatch",
  frontendErrors: "Frontend error telemetry",
  masterInsights: "Master insights",
};

function isRequiresProError(error: unknown) {
  if (!error || typeof error !== "object") return false;
  const payload = (error as { payload?: { error?: string } | null }).payload;
  if (payload && typeof payload === "object" && payload.error === "requires_pro") return true;
  if ((error as { error?: string }).error === "requires_pro") return true;
  if ((error as { code?: string }).code === "capability_required") return true;
  const message = (error as { message?: string }).message;
  if (typeof message === "string" && message.includes("requires_pro")) return true;
  return false;
}

export default function ExecutiveDashboard() {
  const [summary, setSummary] = useState<ExecutiveSummary | null>(null);
  const [stalled, setStalled] = useState<StalledThread[]>([]);
  const [timeouts, setTimeouts] = useState<HitlTimeout[]>([]);
  const [goals, setGoals] = useState<ExecutiveGoal[]>([]);
  const [capabilityDebug, setCapabilityDebug] = useState<CapabilityDebugPayload | null>(null);
  const [versionHealth, setVersionHealth] = useState<VersionHealthStatus | null>(null);
  const [versionBannerDismissed, setVersionBannerDismissed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [autopilotStatus, setAutopilotStatus] = useState<string | null>(null);
  const [autopilotError, setAutopilotError] = useState<string | null>(null);
  const [runningAutopilot, setRunningAutopilot] = useState(false);
  const [ruleId, setRuleId] = useState("");
  const { run: runMasterAutopilot, status: masterAutopilotStatus, error: masterAutopilotError, loading: runningMasterAutopilot } =
    useMasterAutopilot();
  const { openUpgradeModal, hasFullAccess } = useCapabilityContext();
  const canViewDashboard = useHasCapability("canViewExecutiveDashboard");
  const canViewWorkflowStats = useHasCapability("canViewWorkflowStats");
  const canViewStalledThreads = useHasCapability("canViewStalledThreads");
  const canViewHitlTimeouts = useHasCapability("canViewHitlTimeouts");
  const canViewExecutiveGoals = useHasCapability("canViewExecutiveGoals");
  const canViewStalledThreadsKpi = canViewStalledThreads;
  const canViewHitlKpi = canViewHitlTimeouts;
  const canViewGoalsKpi = canViewExecutiveGoals;
  const canRunAutopilot = useHasCapability("canRunAutopilot");
  const canDispatchAutopilotRule = useHasCapability("canDispatchAutopilotRule");
  const canRunMasterSweep = useHasCapability("canRunMasterAutopilot");
  const canSendMasterDigest = useHasCapability("canRunMasterDigest");
  const canViewMasterControl = useHasCapability("canViewMasterControl");
  const canViewMasterInsightsFlag = useHasCapability("canViewMasterInsights");
  const canViewFrontendErrors = useHasCapability("observability.frontend");
  const masterInsightsEnabled =
    hasFullAccess ||
    canRunMasterSweep ||
    canSendMasterDigest ||
    canViewMasterControl ||
    canViewMasterInsightsFlag;
  const { agents: masterAgents } = useMasterAgents({ enabled: masterInsightsEnabled });
  const emailSummary = masterInsightsEnabled ? masterAgents.find((entry) => entry.agent === "email") : null;

  const refresh = useCallback(
    async ({ userInitiated = false }: { userInitiated?: boolean } = {}) => {
      if (!canViewDashboard) {
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      let upgradeShown = false;
      const maybePrompt = (feature: string) => {
        if (upgradeShown) return;
        upgradeShown = true;
        openUpgradeModal(feature);
      };
      const tasks: Promise<void>[] = [];
      const enqueue = <T,>(allowed: boolean, feature: string, action: () => Promise<T>, onSuccess: (value: T) => void) => {
        if (!allowed) {
          if (userInitiated) {
            maybePrompt(feature);
          }
          return;
        }
        tasks.push(
          action()
            .then(onSuccess)
            .catch((err) => {
              if (isRequiresProError(err)) {
                maybePrompt(feature);
                return;
              }
              throw err;
            }),
        );
      };

      enqueue(canViewWorkflowStats, FEATURE_LABELS.workflowStats, fetchExecutiveSummary, (value) => setSummary(value));
      enqueue(canViewStalledThreads, FEATURE_LABELS.stalledThreads, fetchStalledThreads, (value) => setStalled(value));
      enqueue(canViewHitlTimeouts, FEATURE_LABELS.hitlTimeouts, fetchHitlTimeouts, (value) => setTimeouts(value));
      enqueue(canViewExecutiveGoals, FEATURE_LABELS.goals, fetchExecutiveGoals, (value) => {
        setGoals(value.goals);
        setCapabilityDebug(value.capability_debug ?? null);
      });
      enqueue(canViewFrontendErrors, FEATURE_LABELS.frontendErrors, fetchVersionHealthStatus, (value) => {
        setVersionHealth(value);
        setVersionBannerDismissed(false);
      });

      if (tasks.length === 0) {
        setLoading(false);
        return;
      }

      try {
        await Promise.all(tasks);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setLoading(false);
      }
    },
    [canViewDashboard, canViewWorkflowStats, canViewStalledThreads, canViewHitlTimeouts, canViewExecutiveGoals, canViewFrontendErrors, openUpgradeModal],
  );

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (!canViewWorkflowStats) {
      setSummary(null);
    }
  }, [canViewWorkflowStats]);

  useEffect(() => {
    if (!canViewStalledThreads) {
      setStalled([]);
    }
  }, [canViewStalledThreads]);

  useEffect(() => {
    if (!canViewHitlTimeouts) {
      setTimeouts([]);
    }
  }, [canViewHitlTimeouts]);

  useEffect(() => {
    if (!canViewExecutiveGoals) {
      setGoals([]);
      setCapabilityDebug(null);
    }
  }, [canViewExecutiveGoals]);

  useEffect(() => {
    if (!canViewFrontendErrors) {
      setVersionHealth(null);
      setVersionBannerDismissed(false);
    }
  }, [canViewFrontendErrors]);

  const handleEvent = useCallback(
    (event: { event: string }) => {
      if (["thread_update", "hitl_pending", "autopilot_trigger"].includes(event.event)) {
        refresh();
      }
    },
    [refresh],
  );

  useEventStream(handleEvent);

  const promoNeeded = useMemo(() => goals.filter((goal) => goal.status === "promo_needed").length, [goals]);

  const handleRunAutopilot = async () => {
    if (!canRunAutopilot) {
      openUpgradeModal(FEATURE_LABELS.autopilotPass);
      return;
    }
    setRunningAutopilot(true);
    setAutopilotError(null);
    try {
      const result = await runAutopilotPass();
      setAutopilotStatus(`Dispatched ${result.dispatched} tasks`);
    } catch (err) {
      if (isRequiresProError(err)) {
        openUpgradeModal(FEATURE_LABELS.autopilotPass);
      } else {
        setAutopilotError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      setRunningAutopilot(false);
    }
  };

  const handleTestRule = async () => {
    if (!canDispatchAutopilotRule || !ruleId) {
      if (!canDispatchAutopilotRule) {
        openUpgradeModal(FEATURE_LABELS.autopilotRule);
      }
      return;
    }
    setRunningAutopilot(true);
    setAutopilotError(null);
    try {
      await testAutopilotRule(ruleId);
      setAutopilotStatus(`Dispatched rule ${ruleId}`);
      setRuleId("");
    } catch (err) {
      if (isRequiresProError(err)) {
        openUpgradeModal(FEATURE_LABELS.autopilotRule);
      } else {
        setAutopilotError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      setRunningAutopilot(false);
    }
  };

  const handleRunMasterAutopilot = useCallback(() => {
    if (!canRunAutopilot) {
      openUpgradeModal(FEATURE_LABELS.autopilotPass);
      return;
    }
    void runMasterAutopilot().then(() => refresh()).catch(() => {});
  }, [canRunAutopilot, openUpgradeModal, refresh, runMasterAutopilot]);

  const { run: runMasterDigest, status: masterDigestStatus, error: masterDigestError, loading: runningMasterDigest } =
    useMasterDigest();
  return (
    <CapabilityGate
      flag="canViewExecutiveDashboard"
      fallback={<LockedFeatureCard flag="canViewExecutiveDashboard" featureId={FEATURE_LABELS.dashboard} />}
    >
      <div className="aa-page-shell space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="aa-page-title">
              <span className="aa-gradient-text">Executive Dashboard</span>
            </h1>
            <p className="text-sm text-muted-foreground">System-wide signals and goals.</p>
          </div>
          <Button type="button" variant="ghost" size="sm" className="text-xs" onClick={() => refresh({ userInitiated: true })}>
            Refresh
          </Button>
        </div>
        {versionHealth?.status === "frozen" && !versionBannerDismissed && (
          <div className="rounded-xl border border-rose-500/50 bg-rose-500/10 p-4 text-sm text-rose-100">
            <p className="font-semibold">⚠️ Current deployment flagged as unstable.</p>
            <p className="text-xs text-rose-200">
              Version {versionHealth.version} exceeded safe error thresholds. Rollout has been frozen until the issue is resolved.
            </p>
            <div className="mt-3 flex flex-wrap gap-2 text-xs">
              <a className="rounded border border-rose-300 px-3 py-1" href="/observability/frontend">
                View logs
              </a>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-rose-200 hover:text-rose-50"
                onClick={() => setVersionBannerDismissed(true)}
              >
                Dismiss
              </Button>
            </div>
          </div>
        )}
        {error && <p className="text-sm text-rose-600 dark:text-rose-300">{error}</p>}
        <AutopilotControls
          canRun={canRunAutopilot}
          canDispatch={canDispatchAutopilotRule}
          running={runningAutopilot}
          status={autopilotStatus}
          error={autopilotError}
          ruleId={ruleId}
          setRuleId={setRuleId}
          onRun={handleRunAutopilot}
          onTestRule={handleTestRule}
        />
        <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
          <CapabilityGate
            capability="canRunAutopilot"
            fallback={
              <LockedFeatureCard
                flag="canRunAutopilot"
                title="Master autopilot locked"
                description="Upgrade to trigger orchestrations across all agents."
                featureId={FEATURE_LABELS.autopilotPass}
                className="w-auto rounded-full border border-rose-400 px-3 py-1 text-xxs uppercase tracking-wide text-rose-100"
              />
            }
          >
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="rounded-full uppercase tracking-wide text-xs"
              onClick={handleRunMasterAutopilot}
              disabled={runningMasterAutopilot}
            >
              {runningMasterAutopilot ? "Running autopilot…" : "Run master autopilot"}
            </Button>
          </CapabilityGate>
          <CapabilityGate
            capability="canRunMasterDigest"
            fallback={
              <LockedFeatureCard
                flag="canRunMasterDigest"
                title="Master digest locked"
                description="Upgrade to send owner digests."
                className="w-auto rounded-full border border-border/60 px-3 py-1 text-xxs uppercase tracking-wide text-foreground/80"
              />
            }
          >
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="rounded-full uppercase tracking-wide text-xs"
              onClick={() => runMasterDigest()}
              disabled={runningMasterDigest}
            >
              {runningMasterDigest ? "Sending digest…" : "Send test digest"}
            </Button>
          </CapabilityGate>
          {masterAutopilotStatus && <span className="text-foreground">{masterAutopilotStatus}</span>}
          {masterDigestStatus && <span className="text-foreground/80">{masterDigestStatus}</span>}
          {masterAutopilotError && <span className="text-xs text-rose-300">{masterAutopilotError}</span>}
          {masterDigestError && <span className="text-xs text-rose-300">{masterDigestError}</span>}
        </div>
        {masterInsightsEnabled && emailSummary && (
          <CapabilityGate
            capability="canUseEmailAI"
            fallback={
              <LockedFeatureCard
                flag="canUseEmailAI"
                title="Email Agent locked"
                description="Upgrade to empower AI-assisted inbox triage and follow-ups."
              />
            }
          >
            <section className="aa-glass-card rounded-2xl p-4 text-sm text-foreground/80">
              <p className="text-xs uppercase tracking-wide text-primary">Email Agent</p>
              <p className="text-base font-semibold text-foreground">{emailSummary.agent}</p>
              <p className="text-xs text-muted-foreground">
                Tasks: {emailSummary.open_tasks} · HITL: {emailSummary.hitl_pending} · Events: {emailSummary.event_count}
              </p>
            </section>
          </CapabilityGate>
        )}
        {masterInsightsEnabled ? (
          <>
            <section className="grid gap-4 lg:grid-cols-3">
              <MasterAgentsPanel enabled={masterInsightsEnabled} />
              <MasterHitlPanel enabled={masterInsightsEnabled} />
              <MasterTimelinePanel enabled={masterInsightsEnabled} />
            </section>
            <section className="grid gap-4 md:grid-cols-2">
              <MasterMemoryPanel enabled={masterInsightsEnabled} />
              <MasterNotificationsPanel enabled={masterInsightsEnabled} />
            </section>
          </>
        ) : (
          <LockedFeatureCard
            flag="canViewMasterInsights"
            featureId={FEATURE_LABELS.masterInsights}
            title="Master insights locked"
            description="Upgrade to monitor every agent, HITL queue, and unified notifications from one console."
            className="w-full"
          />
        )}
        <section className="grid gap-4 md:grid-cols-4">
          {canViewWorkflowStats ? (
            <Stat
              label="Active workflows"
              value={summary?.delegations_in_flight ?? summary?.total_active_threads ?? 0}
              highlight={(summary?.delegations_in_flight ?? summary?.total_active_threads ?? 0) > 0}
            />
          ) : (
            <LockedKpiCard
              title="Workflow stats locked"
              description="Upgrade to monitor delegations and active automations in real time."
              onUpgrade={() => openUpgradeModal(FEATURE_LABELS.workflowStats)}
            />
          )}
          {canViewStalledThreadsKpi ? (
            <Stat label="Stalled threads" value={stalled.length} highlight={stalled.length > 0} />
          ) : (
            <LockedKpiCard
              title="Stalled thread KPIs"
              description="See which agents are blocked and why once you unlock thread analytics."
              onUpgrade={() => openUpgradeModal(FEATURE_LABELS.stalledThreads)}
            />
          )}
          {canViewHitlKpi ? (
            <Stat label="HITL timeouts" value={timeouts.length} highlight={timeouts.length > 0} />
          ) : (
            <LockedKpiCard
              title="HITL timeout KPIs"
              description="Track overdue approvals and reviewer load by upgrading to Pro."
              onUpgrade={() => openUpgradeModal(FEATURE_LABELS.hitlTimeouts)}
            />
          )}
          {canViewGoalsKpi ? (
            <Stat label="Promo-needed goals" value={promoNeeded} highlight={promoNeeded > 0} />
          ) : (
            <LockedKpiCard
              title="Goals KPIs locked"
              description="Unlock promo-ready goal tracking, completion statuses, and credits."
              onUpgrade={() => openUpgradeModal(FEATURE_LABELS.goals)}
            />
          )}
        </section>
        <div className="grid gap-4 md:grid-cols-2">
          {canViewStalledThreads ? (
            <Panel title="Stalled Threads" loading={loading} emptyMessage="No stalled threads.">
              {stalled.map((thread) => (
                <article key={thread.thread_id} className="aa-glass-card rounded-xl p-4">
                  <div className="text-sm font-semibold text-foreground">{thread.agent}</div>
                  <p className="text-xs text-muted-foreground">{thread.thread_id}</p>
                  <ul className="text-xs text-red-300 mt-2 list-disc ml-5">
                    {thread.reasons?.map((reason, idx) => (
                      <li key={`${thread.thread_id}-${idx}`}>{reason}</li>
                    ))}
                  </ul>
                </article>
              ))}
            </Panel>
          ) : (
            <LockedFeatureCard
              flag="canViewStalledThreads"
              title="Stalled thread insights locked"
              description="Upgrade to drill into blocked conversations, root-cause callouts, and agent health."
              featureId={FEATURE_LABELS.stalledThreads}
              className="h-full"
            />
          )}
          {canViewHitlTimeouts ? (
            <Panel title="HITL Overdue" loading={loading} emptyMessage="No overdue approvals.">
              {timeouts.map((item) => (
                <article key={item.id} className="aa-glass-card rounded-xl p-4">
                  <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                    <span>{item.tool_id ?? "unknown tool"}</span>
                    <span className="text-red-300">{item.age_sec ? `${item.age_sec}s overdue` : ""}</span>
                  </div>
                  <pre className="text-xs text-foreground/70 bg-background rounded p-3 overflow-x-auto">
                    {JSON.stringify(item.payload ?? {}, null, 2)}
                  </pre>
                </article>
              ))}
            </Panel>
          ) : (
            <LockedFeatureCard
              flag="canViewHitlTimeouts"
              title="HITL timeout monitoring locked"
              description="See reviewer bottlenecks, pending approvals, and escalations once you upgrade."
              featureId={FEATURE_LABELS.hitlTimeouts}
              className="h-full"
            />
          )}
        </div>
        {canViewExecutiveGoals ? (
          <Panel title="Goals" loading={loading} emptyMessage="No goals configured.">
            {goals.map((goal) => (
              <article key={goal.id} className="aa-glass-card rounded-xl p-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-semibold text-foreground">{goal.title}</span>
                  <span className="text-muted-foreground">{goal.status}</span>
                </div>
                {goal.deadline && <div className="text-xs text-muted-foreground">Due {new Date(goal.deadline).toLocaleDateString()}</div>}
              </article>
            ))}
            <CapabilityDebugCard data={capabilityDebug} />
          </Panel>
        ) : (
          <LockedFeatureCard
            flag="canViewExecutiveGoals"
            title="Executive goals locked"
            description="Unlock cross-team goals, promo opportunities, and capability telemetry."
            featureId={FEATURE_LABELS.goals}
          />
        )}
      </div>
    </CapabilityGate>
  );
}

function Stat({ label, value, highlight }: { label: string; value: number; highlight?: boolean }) {
  return (
    <div className={`aa-glass-card rounded-2xl p-4 ${highlight ? "border-rose-500/60" : ""}`}>
      <div className="text-sm text-muted-foreground">{label}</div>
      <div className={`text-2xl font-semibold ${highlight ? "text-rose-600 dark:text-rose-300" : "text-foreground"}`}>{value}</div>
    </div>
  );
}

function LockedKpiCard({ title, description, onUpgrade }: { title: string; description: string; onUpgrade: () => void }) {
  return (
    <div className="aa-glass-card flex flex-col justify-between rounded-2xl border-primary/30 p-4 text-sm text-foreground">
      <div>
        <p className="text-sm font-semibold text-foreground">{title}</p>
        <p className="mt-1 text-xs text-muted-foreground">{description}</p>
      </div>
      <Button
        type="button"
        variant="link"
        size="sm"
        className="mt-4 h-auto px-0 text-xs"
        onClick={onUpgrade}
      >
        Upgrade to unlock
      </Button>
    </div>
  );
}

function AutopilotControls({
  canRun,
  canDispatch,
  running,
  status,
  error,
  ruleId,
  setRuleId,
  onRun,
  onTestRule,
}: {
  canRun: boolean;
  canDispatch: boolean;
  running: boolean;
  status: string | null;
  error: string | null;
  ruleId: string;
  setRuleId: Dispatch<SetStateAction<string>>;
  onRun: () => void;
  onTestRule: () => void;
}) {
  const anyAccess = canRun || canDispatch;
  return (
    <section className="aa-glass-card rounded-2xl p-4 space-y-4">
      <div>
        <h2 className="aa-page-title text-lg font-semibold">Autopilot Controls</h2>
        <p className="text-xs text-muted-foreground">Trigger manual passes or run targeted rules across your workflows.</p>
      </div>
      <div className="space-y-3">
        {canRun ? (
          <Button type="button" onClick={onRun} disabled={running} className="w-full">
            {running ? "Running…" : "Run Autopilot Pass"}
          </Button>
        ) : (
          <LockedFeatureCard
            flag="canRunAutopilot"
            title="Autopilot sweeps locked"
            description="Unlock manual autopilot passes to trigger orchestrations on demand."
            featureId={FEATURE_LABELS.autopilotPass}
            className="w-full"
          />
        )}
        {canDispatch ? (
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <input
              type="text"
              value={ruleId}
              onChange={(event) => setRuleId(event.target.value)}
              className="flex-1 rounded border border-border bg-background/70 px-3 py-2 text-sm text-foreground"
              placeholder="Rule ID"
              disabled={!canDispatch || running}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="uppercase tracking-wide text-xs"
              onClick={onTestRule}
              disabled={!canDispatch || running || !ruleId}
            >
              Dispatch Rule
            </Button>
          </div>
        ) : (
          <LockedFeatureCard
            flag="canDispatchAutopilotRule"
            title="Autopilot rule dispatch locked"
            description="Upgrade to test and fire single rules without running a full sweep."
            featureId={FEATURE_LABELS.autopilotRule}
            className="w-full"
          />
        )}
      </div>
      {status && anyAccess && <p className="text-xs text-emerald-600 dark:text-emerald-300">{status}</p>}
      {error && anyAccess && <p className="text-xs text-rose-600 dark:text-rose-300">{error}</p>}
      {!anyAccess && (
        <p className="text-xs text-muted-foreground">
          Upgrade to Pro to run global sweeps, dispatch individual rules, and review autopilot execution logs.
        </p>
      )}
    </section>
  );
}

function CapabilityDebugCard({ data }: { data: CapabilityDebugPayload | null }) {
  if (!data) {
    return (
      <div className="aa-glass-card rounded-2xl p-4 text-sm text-muted-foreground">
        <p className="text-xs uppercase tracking-wide text-muted-foreground">Capability Profile</p>
        <p>Plan sync pending.</p>
      </div>
    );
  }
  const enabled = Object.entries(data.capabilities ?? {})
    .filter(([, allowed]) => Boolean(allowed))
    .map(([flag]) => flag);
  const highlighted = enabled.slice(0, 6);
  return (
    <div className="aa-glass-card mt-6 rounded-2xl border-primary/30 p-4 text-sm text-foreground/80">
      <p className="text-xs uppercase tracking-wide text-primary">Capability profile</p>
      <div className="mt-2 grid gap-3 md:grid-cols-2">
        <div>
          <p className="text-xs text-muted-foreground">Plan</p>
          <p className="text-sm text-foreground">{planLabel(data.tier)}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Org plan</p>
          <p className="text-sm text-foreground">{planLabel(data.organization_tier)}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Credits</p>
          <p className="text-sm text-foreground">{data.credits ?? 0}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Expires</p>
          <p className="text-sm text-foreground">{data.credit_expires_at ? new Date(data.credit_expires_at).toLocaleDateString() : "—"}</p>
        </div>
      </div>
      <p className="mt-3 text-xs text-muted-foreground">
        Enabled: {highlighted.length ? highlighted.join(", ") : "base access"}
        {enabled.length > highlighted.length ? ` +${enabled.length - highlighted.length} more` : ""}
      </p>
    </div>
  );
}

function Panel({ title, children, loading, emptyMessage }: { title: string; children: React.ReactNode; loading: boolean; emptyMessage: string }) {
  const hasContent = Array.isArray(children) ? children.length > 0 : Boolean(children);
  return (
    <section className="aa-glass-card rounded-2xl p-4 space-y-3">
      <h2 className="aa-page-title text-lg font-semibold">{title}</h2>
      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : hasContent ? (
        children
      ) : (
        <p className="text-sm text-muted-foreground">{emptyMessage}</p>
      )}
    </section>
  );
}
